import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  FormHelperText,
  Stack,
  CircularProgress,
  InputAdornment,
  Alert,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import packageService from '../services/packageService';
import routerService from '../services/routerService';
import spopService from '../services/spopService';
import { Router } from '../types';

interface EditPackageDialogProps {
  open: boolean;
  onClose: () => void;
  packageId: string | null;
  onSuccess?: (message: string) => void;
}

interface PackageFormData {
  name: string;
  profile_name: string;
  bandwidth_upload: string;
  bandwidth_download: string;
  price: string;
  validity_days: string;
  quota_bytes: string;
  burst_limit: string;
  burst_threshold: string;
  burst_time: string;
  priority: string;
  status: 'active' | 'inactive';
  description: string;
  spop_id: string;
}

const emptyForm: PackageFormData = {
  name: '',
  profile_name: '',
  bandwidth_upload: '',
  bandwidth_download: '',
  price: '',
  validity_days: '30',
  quota_bytes: '',
  burst_limit: '',
  burst_threshold: '',
  burst_time: '',
  priority: '8',
  status: 'active',
  description: '',
  spop_id: '',
};

const getApiError = (error: unknown) => {
  const err = error as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message || err?.message || 'Terjadi kesalahan';
};

const EditPackageDialog: React.FC<EditPackageDialogProps> = ({
  open,
  onClose,
  packageId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<PackageFormData>(emptyForm);
  const [errors, setErrors] = useState<Partial<PackageFormData>>({});
  const [selectedRouter, setSelectedRouter] = useState<any>(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [availableProfiles, setAvailableProfiles] = useState<any[]>([]);
  const queryClient = useQueryClient();

  const { data: packageData, isLoading, error: loadError } = useQuery({
    queryKey: ['package-detail', packageId],
    queryFn: () => packageService.getPackage(packageId!),
    enabled: open && !!packageId,
  });

  const { data: routersData } = useQuery({
    queryKey: ['routers-edit-package'],
    queryFn: () => routerService.getRouters(1, 100),
    enabled: open,
  });

  const { data: spopsData } = useQuery({
    queryKey: ['spops-all-edit-package'],
    queryFn: async () => {
      const response = await spopService.getAllSPOPs();
      return response.data?.data?.spops || [];
    },
    enabled: open,
  });

  useEffect(() => {
    if (!packageData || !open) return;
    setFormData({
      name: packageData.name || '',
      profile_name: packageData.profile_name || '',
      bandwidth_upload: packageData.bandwidth_upload || '',
      bandwidth_download: packageData.bandwidth_download || '',
      price: packageData.price != null ? String(Math.round(Number(packageData.price))) : '',
      validity_days: packageData.validity_days?.toString() || '30',
      quota_bytes: packageData.quota_bytes
        ? String(Math.round(Number(packageData.quota_bytes) / (1024 * 1024 * 1024)))
        : '',
      burst_limit: packageData.burst_limit || '',
      burst_threshold: packageData.burst_threshold || '',
      burst_time: packageData.burst_time || '',
      priority: packageData.priority?.toString() || '8',
      status: (packageData.status as 'active' | 'inactive') || 'active',
      description: packageData.description || '',
      spop_id: packageData.spop_id || '',
    });
    setErrors({});
    setSelectedRouter(null);
    setAvailableProfiles([]);
  }, [packageData, open]);

  const updatePackageMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      packageService.updatePackage(packageId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      queryClient.invalidateQueries({ queryKey: ['package-detail', packageId] });
      onSuccess?.('Paket berhasil diperbarui');
      handleClose();
    },
  });

  const handleClose = () => {
    setErrors({});
    setSelectedRouter(null);
    setAvailableProfiles([]);
    onClose();
  };

  const handleInputChange = (field: keyof PackageFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSelectChange = (field: keyof PackageFormData) => (event: { target: { value: string } }) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<PackageFormData> = {};
    if (!formData.name.trim()) newErrors.name = 'Nama paket wajib diisi';
    if (!formData.profile_name.trim()) newErrors.profile_name = 'Profile wajib diisi';
    if (!formData.price.trim() || Number(formData.price) <= 0) newErrors.price = 'Harga harus angka positif';
    if (!formData.validity_days.trim() || Number(formData.validity_days) <= 0) {
      newErrors.validity_days = 'Masa berlaku harus angka positif';
    }
    if (formData.quota_bytes && Number(formData.quota_bytes) < 0) {
      newErrors.quota_bytes = 'Kuota tidak valid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!packageId || !validateForm()) return;
    updatePackageMutation.mutate({
      name: formData.name.trim(),
      profile_name: formData.profile_name.trim(),
      bandwidth_upload: formData.bandwidth_upload.trim() || null,
      bandwidth_download: formData.bandwidth_download.trim() || null,
      price: Number(formData.price),
      validity_days: Number(formData.validity_days),
      quota_bytes: formData.quota_bytes
        ? Math.round(Number(formData.quota_bytes) * 1024 * 1024 * 1024)
        : null,
      burst_limit: formData.burst_limit.trim() || null,
      burst_threshold: formData.burst_threshold.trim() || null,
      burst_time: formData.burst_time.trim() || null,
      priority: Number(formData.priority),
      status: formData.status,
      description: formData.description.trim() || null,
      spop_id: formData.spop_id || null,
    });
  };

  const handleRouterSelect = async (router: Router) => {
    setSelectedRouter(router);
    setLoadingProfiles(true);
    setAvailableProfiles([]);
    try {
      const profiles = await routerService.getPPPProfiles(String(router.id));
      setAvailableProfiles(profiles || []);
    } catch {
      setAvailableProfiles([]);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleProfileFromRouter = (profileName: string) => {
    setFormData((prev) => ({ ...prev, profile_name: profileName }));
    const profile = availableProfiles.find((p) => p.name === profileName);
    if (profile?.rateLimit) {
      const parts = String(profile.rateLimit).split('/');
      if (parts.length === 2) {
        setFormData((prev) => ({
          ...prev,
          profile_name: profileName,
          bandwidth_upload: parts[0]?.trim() || prev.bandwidth_upload,
          bandwidth_download: parts[1]?.trim() || prev.bandwidth_download,
        }));
        return;
      }
    }
    if (errors.profile_name) setErrors((prev) => ({ ...prev, profile_name: undefined }));
  };

  const formatPriceDisplay = (value: string) => {
    const num = value.replace(/\D/g, '');
    if (!num) return '';
    return new Intl.NumberFormat('id-ID').format(Number(num));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6">Edit Paket Internet</Typography>
        {packageData && (
          <Typography variant="body2" color="text.secondary">
            {packageData.name}
          </Typography>
        )}
        <IconButton onClick={handleClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Gagal memuat paket: {getApiError(loadError)}
          </Alert>
        )}

        {updatePackageMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Gagal menyimpan: {getApiError(updatePackageMutation.error)}
          </Alert>
        )}

        {!isLoading && !loadError && packageData && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Informasi Dasar
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Nama Paket"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                />

                <FormControl fullWidth>
                  <InputLabel>SPOP (Opsional)</InputLabel>
                  <Select
                    value={formData.spop_id}
                    label="SPOP (Opsional)"
                    onChange={handleSelectChange('spop_id')}
                  >
                    <MenuItem value="">
                      <em>Semua SPOP</em>
                    </MenuItem>
                    {spopsData?.map((spop: { id: string; name: string; areaCode?: string }) => (
                      <MenuItem key={spop.id} value={spop.id}>
                        {spop.name} {spop.areaCode ? `(${spop.areaCode})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Router MikroTik (opsional)</InputLabel>
                  <Select
                    value={selectedRouter ? String(selectedRouter.id) : ''}
                    label="Router MikroTik (opsional)"
                    onChange={(e) => {
                      const router = routersData?.data?.routers?.find(
                        (r) => String(r.id) === e.target.value
                      );
                      if (router) handleRouterSelect(router);
                    }}
                    disabled={loadingProfiles}
                  >
                    <MenuItem value="">
                      <em>Pilih router untuk sync profile</em>
                    </MenuItem>
                    {routersData?.data?.routers?.map((router) => (
                      <MenuItem key={router.id} value={String(router.id)}>
                        {router.name} ({router.ip_address}:{router.port})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Pilih router untuk mengambil daftar profile PPP dari MikroTik
                  </FormHelperText>
                </FormControl>

                {availableProfiles.length > 0 && (
                  <FormControl fullWidth>
                    <InputLabel>Profile dari Router</InputLabel>
                    <Select
                      value=""
                      label="Profile dari Router"
                      onChange={(e) => handleProfileFromRouter(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>-- Pilih --</em>
                      </MenuItem>
                      {availableProfiles.map((profile) => (
                        <MenuItem key={profile.id || profile.name} value={profile.name}>
                          {profile.name}
                          {profile.rateLimit ? ` (${profile.rateLimit})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  label="Profile PPP"
                  value={formData.profile_name}
                  onChange={handleInputChange('profile_name')}
                  error={!!errors.profile_name}
                  helperText={errors.profile_name || 'Nama profile di MikroTik'}
                />

                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    label="Bandwidth Upload"
                    value={formData.bandwidth_upload}
                    onChange={handleInputChange('bandwidth_upload')}
                    placeholder="contoh: 10M"
                  />
                  <TextField
                    fullWidth
                    label="Bandwidth Download"
                    value={formData.bandwidth_download}
                    onChange={handleInputChange('bandwidth_download')}
                    placeholder="contoh: 20M"
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Deskripsi"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  multiline
                  rows={2}
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Harga & Konfigurasi
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    label="Harga Paket"
                    value={formatPriceDisplay(formData.price)}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, '');
                      setFormData((prev) => ({ ...prev, price: raw }));
                    }}
                    error={!!errors.price}
                    helperText={errors.price}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Masa Berlaku"
                    type="number"
                    value={formData.validity_days}
                    onChange={handleInputChange('validity_days')}
                    error={!!errors.validity_days}
                    helperText={errors.validity_days}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">hari</InputAdornment>,
                    }}
                  />
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    label="Kuota (GB)"
                    type="number"
                    value={formData.quota_bytes}
                    onChange={handleInputChange('quota_bytes')}
                    error={!!errors.quota_bytes}
                    helperText={errors.quota_bytes || 'Kosongkan untuk unlimited'}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">GB</InputAdornment>,
                    }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Prioritas</InputLabel>
                    <Select
                      value={formData.priority}
                      label="Prioritas"
                      onChange={handleSelectChange('priority')}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                        <MenuItem key={n} value={String(n)}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={handleSelectChange('status')}
                  >
                    <MenuItem value="active">Aktif</MenuItem>
                    <MenuItem value="inactive">Nonaktif</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={updatePackageMutation.isPending}>
          Batal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !!loadError || updatePackageMutation.isPending}
        >
          {updatePackageMutation.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPackageDialog;
